import { useState } from 'react'

import { Box, Spinner, Wizard, WizardStep } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import {
  AddEmployeeModalDocument,
  PeopleChartAddTeamMemberDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { convertDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { isFeatureAvailabilityEnabled } from 'modules/core/lib/isFeatureAvailabilityEnabled'
import app from 'modules/core/store/models/app'

import { useCreateEmployeeWithContract } from 'modules/employees/hooks/useCreateEmployeeWithContract'
import { unifiedAddEmployeeEvents } from 'modules/employees/lib/events'

import { contractInformationStep } from './ContractInformationStep'
import { parseSalary } from './ContractInformationStep/salaryUtils'
import { contractInformationStepV2 } from './ContractInformationStepV2'
import { CreateEmployeeProps } from './CreateModalProps'
import { devicesStep } from './DevicesStep'
import { generalInformationStep } from './GeneralInformationStep'
import { generalInformationStepV2 } from './GeneralInformationStepV2'
import { generalSettingsStepV2 } from './GeneralSettingsStepV2'
import { onboardingInfoStep } from './OnboardingInfoStep'
import { personalDetailsStep } from './PersonalDetailsStep'
import { personalDetailsStepV2 } from './PersonalDetailsStepV2'
import { workDetailsStep } from './WorkDetailsStep'
import { workDetailsStepV2 } from './WorkDetailsStepV2'
import { parseHours } from './hoursUtils'
import { EmployeeSource, WizardData, genericAmplitudeProperties } from './types'

const CENTS = 100

const prepareSalaryAmountCents = (salaryFromForm: string | undefined) => {
  const salaryAmount = parseSalary(salaryFromForm)
  if (!salaryAmount) return 0
  return parseInt(`${salaryAmount * CENTS}`)
}

const prepareHoursCents = (hoursFromForm: string | undefined) => {
  const hoursAmount = parseHours(hoursFromForm)
  if (!hoursAmount) return undefined
  return parseInt(`${Math.round(hoursAmount * CENTS)}`)
}

const convertDateToBackendFormat = (contractVersionStartsOn: string) => {
  if (!contractVersionStartsOn) return ''

  return convertDate(contractVersionStartsOn, 'ui', 'backend')
}

type Props = {
  onClose: () => void
  source: EmployeeSource
  onSuccess: (employeeId: string) => void | Promise<void>
  onError?: () => void
  initialValues?: WizardData
}

const UnifiedAddEmployeeModal = ({ initialValues, onClose, source, onSuccess, onError }: Props) => {
  const { company } = useCurrentSession()
  const { createEmployeeWithContract } = useCreateEmployeeWithContract()
  const [createTeamMembership] = useMutation(PeopleChartAddTeamMemberDocument, {
    extractorFn: (data) => data.teamsMutations.createTeamsMembership,
  })
  const companyCountries = company.legalEntities.map((entity) => entity.country)

  const { data, loading: loadingPageData } = useQuery(AddEmployeeModalDocument, {
    variables: {
      companyId: company.id,
      featureAvailabilityIds: [
        Features.compensations_management,
        Features.payroll_management,
        Features.time_tracking_annualisation_daily_value,
        Features.it_management_management,
        Features.payroll_management,
        Features.contracts_discontinuous_contracts,
      ],
      countries: companyCountries,
    },
  })

  const canAssignManager = data?.employees._policies?.assignEmployeeManager.can || false
  const canInviteUserToFactorial = data?.employees._policies?.inviteEmployee.can || false
  const canAssignWorkflow = data?.employees._policies?.assignEmployeeToWorkflow.can || false
  const canAssignPermissionGroup =
    data?.employees._policies?.assignEmployeePermissionGroup.can || false

  const schemaFields = useUnsafeMemo(
    () => data?.dataDispatcher.dataSchemasConnection.nodes || [],
    [data]
  )
  const legalEntities = schemaFields.find(
    (field) => field.fieldName === 'legal_entity_id'
  )?.allowedValues
  const policies = schemaFields.find(
    (field) => field.fieldName === 'payroll_policy_id'
  )?.allowedValues

  const featureAvailabilities = useUnsafeMemo(
    () => data?.companies?.featureAvailabilitiesConnection?.nodes || [],
    [data]
  )
  const hasPayrollApp = data?.companies?.payrollModuleAvailability?.available ?? false
  const hasCompensationsManagement = isFeatureAvailabilityEnabled(
    featureAvailabilities,
    Features.compensations_management
  )
  const hasPayrollManagement = isFeatureAvailabilityEnabled(
    featureAvailabilities,
    Features.payroll_management
  )
  const hasTimeTrackingAnnualisationDailyValue = isFeatureAvailabilityEnabled(
    featureAvailabilities,
    Features.time_tracking_annualisation_daily_value
  )

  const hasItManagementEnabled = data?.companies?.itManagementModuleAvailability?.available ?? false

  const hasJobCatalog =
    data?.features?.featuresConnection.nodes.find((feature) => feature.name === 'job_catalog')
      ?.enabled ?? false
  const hasDevTimeTrackingWorkableDaysDefinition =
    data?.features?.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_time_tracking_workable_days_definition'
    )?.enabled ?? false
  const hasDevTimeoffWorkConditions =
    data?.features?.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_timeoff_work_conditions'
    )?.enabled ?? false
  const hasLegacyJobCatalog =
    data?.features?.featuresConnection.nodes.find(
      (feature) => feature.name === 'legacy_job_catalog'
    )?.enabled ?? false
  const hasDevJobCatalogTree = !hasLegacyJobCatalog
  const hasLegacyContractStructure =
    data?.features?.featuresConnection.nodes.find(
      (feature) => feature.name === 'seasonal_contracts_disabled'
    )?.enabled ?? false

  const hasFixedDiscontinuousContracts = featureAvailabilities.some(
    (f) =>
      f.id === 'contracts_discontinuous_contracts' && f.available && !hasLegacyContractStructure
  )

  const isHiringPlanEnabled =
    data?.ats?._policies?.sidebarSeeRecruitmentHiringPlanSubLevelItem?.can ?? false

  const jobCatalogTrees = data?.jobCatalog.treesConnection.nodes
  const defaultLegalEntity = legalEntities?.find((entity) => entity.default)?.key || ''
  const defaultLegalEntityCountry = legalEntities?.find((entity) => entity.default)?.country || ''
  const defaultPayrollPolicyId = policies?.find((policy) => policy.default)?.key || ''
  const initialData: WizardData = useUnsafeMemo(
    () => ({
      // Hardcoded/data defaults (overridable by callers via initialValues)
      sendInvitation: true,
      withOnboarding: false,
      contractIsDiscontinuous: false,
      legalEntityId: defaultLegalEntity,
      legalEntityCountry: defaultLegalEntityCountry,
      payrollPolicyId: hasCompensationsManagement ? undefined : defaultPayrollPolicyId,
      source: source,
      contractsSalaryFrequency: 'yearly',
      hasTrialPeriod: false,
      companyId: company.id.toString(),
      // Caller overrides
      ...initialValues,
      // Query-derived flags (always win, not overridable)
      hasCompensationsManagement,
      hasPayrollApp: hasPayrollManagement ? false : hasPayrollApp,
      hasJobCatalog,
      canAssignManager,
      canInviteUserToFactorial,
      canAssignPermissionGroup,
      canAssignWorkflow,
      fieldsSchema: schemaFields,
      hasDevTimeTrackingWorkableDaysDefinition,
      hasDevTimeoffWorkConditions,
      hasTimeTrackingAnnualisationDailyValue: hasTimeTrackingAnnualisationDailyValue,
      hasDevJobCatalogTree,
      hasFixedDiscontinuousContracts,
      jobCatalogTrees,
      isHiringPlanEnabled,
    }),
    [
      company.id,
      hasCompensationsManagement,
      defaultPayrollPolicyId,
      hasPayrollManagement,
      hasPayrollApp,
      hasJobCatalog,
      canAssignManager,
      canInviteUserToFactorial,
      canAssignPermissionGroup,
      canAssignWorkflow,
      schemaFields,
      hasDevTimeTrackingWorkableDaysDefinition,
      hasDevTimeoffWorkConditions,
      hasTimeTrackingAnnualisationDailyValue,
      hasDevJobCatalogTree,
      hasFixedDiscontinuousContracts,
      jobCatalogTrees,
      isHiringPlanEnabled,
      defaultLegalEntity,
      defaultLegalEntityCountry,
      source,
      initialValues,
    ]
  )

  const [wizardData, setWizardData] = useState<WizardData>(initialData)

  const handleOnDataChanged = (newData: WizardData) => {
    setWizardData({ ...wizardData, ...newData })
  }

  const createEmployee = async ({
    employee,
    onSuccess: onSuccessCb,
    onError: onErrorCb,
  }: CreateEmployeeProps) => {
    const result = await createEmployeeWithContract(employee)

    const handled = handleResult(result, (formErrors: Record<string, string>) => {
      onErrorCb && onErrorCb(formErrors)
    })

    if (handled.success) {
      const newEmployee = handled.data.employee
      if (newEmployee) {
        onSuccessCb && onSuccessCb({ id: newEmployee.id })
        track(unifiedAddEmployeeEvents.submitEmployeeModal, genericAmplitudeProperties(wizardData))
      }

      employee.requestedDeviceAssets.forEach((_) => {
        track(trackingEvents.itManagement.deviceCatalogEvents.requestDeviceSuccess, {
          company_id: company.id,
          employee_id: newEmployee?.id,
        })
      })

      employee.itAssetIds.forEach((_) => {
        track(trackingEvents.itManagement.assigmentEquipment.submit, {
          origin: 'onboarding_flow',
          assign_to: 'person',
        })
      })
    }
  }

  // Get all team IDs, preferring teamIds array, falling back to single teamId
  const getAllTeamIds = (): string[] => {
    if (wizardData.teamIds?.length) {
      return wizardData.teamIds.filter(Boolean)
    }
    if (wizardData.teamId) {
      return [wizardData.teamId].filter(Boolean)
    }
    return []
  }

  const handleSubmit = async () => {
    const allTeamIds = getAllTeamIds()
    const firstTeamId = allTeamIds[0]

    await createEmployee({
      employee: {
        addressLine1: wizardData.addressLine1 || '',
        city: wizardData.city,
        companyId: company.id,
        contractEffectiveOn: convertDateToBackendFormat(wizardData.contractsStartsOn || ''),
        ...(wizardData.hasFixedDiscontinuousContracts && {
          contractIsDiscontinuous: wizardData.contractIsDiscontinuous || false,
        }),
        contractStartsOn: convertDateToBackendFormat(wizardData.contractsStartsOn || ''),
        endsOn: convertDateToBackendFormat(wizardData.contractsEndsOn || ''),
        country: wizardData.country,
        email: wizardData.email || '',
        firstName: wizardData.firstName || '',
        gender: wizardData.gender,
        identifier: wizardData.identifier,
        identifierType: wizardData.identifierType,
        identifierExpirationDate: wizardData.identifierExpirationDate
          ? convertDateToBackendFormat(wizardData.identifierExpirationDate)
          : undefined,
        lastName: wizardData.lastName || '',
        legalEntityId: wizardData.legalEntityId ?? '',
        employeeGroupId: wizardData.employeeGroupId,
        postalCode: wizardData.postalCode,
        state: wizardData.state,
        sendInvitation: wizardData.sendInvitation || false,
        jobCatalogLevelId: wizardData.jobLevelId || undefined,
        jobCatalogTreeNodeUuid: wizardData.jobCatalogTreeNodeUuid,
        salaryAmountInCents: prepareSalaryAmountCents(wizardData.salaryAmount),
        salaryFrequency: wizardData.contractsSalaryFrequency,
        // Pass only the first team to createEmployeeWithContract
        teamId: firstTeamId || undefined,
        locationId: wizardData.locationId,
        defaultWorkAreaId: wizardData.defaultWorkAreaId || undefined,
        employeeSource: {
          ...source,
          ...(source?.sourceType === 'ats_application' &&
            wizardData.vacancyId && { vacancyId: wizardData.vacancyId }),
        },
        onboardingProcessId: wizardData.onboardingProcessId || undefined,
        withWelcomeSpace: true,
        socialSecurityNumber: wizardData.socialSecurityNumber,
        payrollPolicyId: wizardData.payrollPolicyId || undefined,
        workingHours: prepareHoursCents(wizardData.workingHours),
        workingHoursFrequency: wizardData.workingHoursFrequency,
        workingWeekDays: wizardData.workingDays,
        hasTrialPeriod: wizardData.hasTrialPeriod || false,
        trialPeriodEndsOn: convertDateToBackendFormat(wizardData.trialPeriodEndsOn || ''),
        birthdayOn: convertDateToBackendFormat(wizardData.birthdayOn || ''),
        phoneNumber: wizardData.phoneNumber,
        nationality: wizardData.nationality,
        spainContractFields: {
          contractTypeId: wizardData.contractsEsContractTypeId || undefined,
          workingDayTypeId: wizardData.contractsEsWorkingDayTypeId || undefined,
          professionalCategoryId: wizardData.contractsEsProfessionalCategoryId || undefined,
          contributionTypeId: wizardData.contractsEsContributionTypeId || undefined,
          agreementCodeId: wizardData.contractsEsAgreementCodeId || undefined,
        },
        franceContractFields: {
          contractTypeId: wizardData.contractsFrContractTypeId || undefined,
          professionalCategoryId: wizardData.contractsFrProfessionalCategoryId || undefined,
        },
        portugalContractFields: {
          contractTypeId: wizardData.contractsPtContractTypeId || undefined,
        },
        germanyContractFields: {
          contractTypeId: wizardData.contractsDeContractTypeId || undefined,
          baseSalaryTypeId: wizardData.contractsDeBaseSalaryTypeId || undefined,
        },
        managerId: wizardData.managerId || undefined,
        timeoffManagerId: wizardData.timeoffManagerId || undefined,
        permissionsGroupId: wizardData.permissionsGroupId || undefined,
        bankNumber: wizardData.bankNumber,
        swiftBic: wizardData.swiftBic,
        bankNumberFormat: wizardData.bankNumberFormat,
        contractsBankHolidayTreatment: wizardData.contractsBankHolidayTreatment,
        contractsWorkingTimePercentageInCents: wizardData.contractsWorkingTimePercentageInCents
          ? +wizardData.contractsWorkingTimePercentageInCents
          : undefined,
        contractsMaxLegalYearlyHours: wizardData.contractsMaxLegalYearlyHours
          ? +wizardData.contractsMaxLegalYearlyHours
          : undefined,
        contractsMaximumWeeklyHours: wizardData.contractsMaximumWeeklyHours
          ? +wizardData.contractsMaximumWeeklyHours
          : undefined,
        contractsAnnualWorkingTimeDistribution: wizardData.contractsAnnualWorkingTimeDistribution,
        itAssetIds:
          wizardData.selectedDevices?.map((device) => device.itAssetId).filter(Boolean) || [],
        requestedDeviceAssets: wizardData.requestedDeviceAssets || [],
        requestedDevicesResponsibleEmployeeIds:
          wizardData.requestedDevicesResponsibleEmployeeIds || [],
        requestedDevicesNotes: wizardData.requestedDevicesNotes || '',
      },
      onError: onSubmitError,
      onSuccess: onSubmitSuccess,
    })
  }

  const onSubmitSuccess = async ({ id }: { id: string }) => {
    // Create additional team memberships for teams beyond the first one
    const allTeamIds = getAllTeamIds()
    const additionalTeamIds = allTeamIds.slice(1)

    if (additionalTeamIds.length > 0) {
      await Promise.all(
        additionalTeamIds.map((teamId) =>
          createTeamMembership({ variables: { employeeId: id, teamId } })
        )
      )
    }

    onSuccess(id)
  }

  const onSubmitError = (errors: Record<string, string>) => {
    if (errors) {
      app.setFlash({
        type: 'wrong',
        message:
          errors?.errors ||
          Object.entries(errors)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', '),
        time: 3000,
      })
    }
    track(unifiedAddEmployeeEvents.submitError, {
      errors: errors.errors,
    })

    onError && onError()
  }

  const handleOnClose = () => {
    onClose()
    track(unifiedAddEmployeeEvents.closeAddEmployeeModal, genericAmplitudeProperties(wizardData))
  }

  useUnsafeEffect(() => {
    track(unifiedAddEmployeeEvents.openAddEmployeeModal, genericAmplitudeProperties(initialData))
  }, [initialData])

  if (loadingPageData)
    return (
      <Box flexGrow justifyContent="center" alignItems="center">
        <Spinner />
      </Box>
    )

  const stepsV2: WizardStep<WizardData>[] = [
    generalInformationStepV2(),
    personalDetailsStepV2(),
    workDetailsStepV2(),
    contractInformationStepV2(),
    ...(hasItManagementEnabled ? [devicesStep()] : []),
    ...(canInviteUserToFactorial || canAssignPermissionGroup ? [generalSettingsStepV2()] : []),
  ]
  const _steps: WizardStep<WizardData>[] = [
    generalInformationStep(),
    personalDetailsStep(),
    workDetailsStep(canAssignManager),
    contractInformationStep(),
    ...(canInviteUserToFactorial || canAssignPermissionGroup
      ? [onboardingInfoStep(canAssignPermissionGroup, canInviteUserToFactorial)]
      : []),
  ]

  return (
    <Wizard.Modal
      title={i18n.t('add_employee_modal.title')}
      icon="add-user"
      steps={stepsV2}
      initialData={initialData}
      onSubmit={handleSubmit}
      onClose={handleOnClose}
      onDataChanged={handleOnDataChanged}
    />
  )
}

export default UnifiedAddEmployeeModal
